import { apiUrl } from '@ai-studio/shared';

import { PRISM_SCRIPT_URL } from '../engine/prism.js';
import { readProjectFile } from '../engine/host.js';
import { api } from './api.js';
import { referencedFiles, usedVoices, usesSoundFont, type SoundDoc } from './sound-doc.js';

/**
 * Rendering sound documents for the Sound tab.
 *
 * 1. In the browser: prism_wasm's `SoundRenderer` in a worker. Project files
 *    the document references (samples, IRs, wavetables, `.sf2`, instrument
 *    `ref`s) and cached SoundFont/voice files (`GET /api/sound/cache/file`)
 *    are added to the renderer first (once each, re-added when they change).
 * 2. Fallback: the backend route `POST /api/projects/:id/sound/render`
 *    (`prism sound render`) when the browser engine is missing or reports a
 *    feature it does not support (the file is saved before).
 */

export interface RenderedAudio {
  left: Float32Array;
  right: Float32Array;
  rate: number;
}

/** Analysis object from prism_sound (`analysis::analyze`), kept as sent. */
export type SoundAnalysis = Record<string, unknown>;

export interface SoundRenderResult {
  audio: RenderedAudio;
  analysis: SoundAnalysis | null;
  warnings: string[];
  source: 'wasm' | 'server';
  /** Image URLs (object/data URLs or server paths) when the renderer made them. */
  spectrogram: string | null;
  waveform: string | null;
}

export interface RenderRequest {
  projectId: number;
  path: string;
  doc: SoundDoc;
  variant?: string | null;
  /** Saves the document so the backend renders the latest edit. */
  save?: () => Promise<void>;
  onProgress?: (stage: string, fraction: number | null) => void;
  /** Skip the browser engine (e.g. the user asked for a full CLI render). */
  serverOnly?: boolean;
  /** Also make the renderer's spectrogram PNG (browser renders). */
  spectrogram?: boolean;
}

export const SPECTROGRAM_SIZE: [number, number] = [1200, 256];
const UNSUPPORTED = /unsupported|not supported|not available|unavailable in the browser|wasm|browser build|requires the (cli|backend|desktop)/i;

/* ------------------------------------------------------------------ */
/* Worker                                                              */

interface WorkerResult {
  type: 'result';
  id: number;
  left: Float32Array;
  right: Float32Array;
  rate: number;
  warnings: string[];
  analysis: unknown;
  spectrogram: Uint8Array | null;
}

let worker: Worker | null = null;
let workerReady: Promise<string | null> | null = null;
let nextId = 1;
const pending = new Map<number, { resolve: (r: WorkerResult) => void; reject: (e: Error & { unsupported?: boolean }) => void }>();
/** path → fingerprint of the bytes the worker has. */
const sentFiles = new Map<string, string>();

function startWorker(): Promise<string | null> {
  if (workerReady) return workerReady;
  workerReady = new Promise((resolve) => {
    try {
      worker = new Worker(new URL('./sound-render.worker.ts', import.meta.url), { type: 'module' });
    } catch (error) {
      resolve(`Could not start the sound worker: ${error instanceof Error ? error.message : String(error)}`);
      return;
    }
    const w = worker;
    w.onmessage = (event: MessageEvent<{ type: string; id?: number; ok?: boolean; reason?: string | null; message?: string; unsupported?: boolean }>) => {
      const msg = event.data;
      if (msg.type === 'ready') {
        resolve(msg.ok ? null : (msg.reason ?? 'sound engine unavailable'));
        return;
      }
      const entry = msg.id === undefined ? undefined : pending.get(msg.id);
      if (!entry) return;
      pending.delete(msg.id!);
      if (msg.type === 'result') entry.resolve(msg as unknown as WorkerResult);
      else entry.reject(Object.assign(new Error(msg.message ?? 'render failed'), { unsupported: msg.unsupported }));
    };
    w.onerror = (event) => {
      resolve(`Sound worker failed: ${event.message || 'unknown error'}`);
      for (const [, p] of pending) p.reject(new Error('sound worker crashed'));
      pending.clear();
    };
    w.postMessage({ type: 'init', url: new URL(PRISM_SCRIPT_URL, location.href).href });
  });
  return workerReady;
}

/** Forget a failed engine load so the next render retries (after engine:build). */
export function resetSoundEngine(): void {
  worker?.terminate();
  worker = null;
  workerReady = null;
  sentFiles.clear();
  for (const [, p] of pending) p.reject(new Error('sound worker restarted'));
  pending.clear();
}

/** A project file changed: it is re-sent before the next browser render. */
export function invalidateSoundFile(path: string): void {
  sentFiles.delete(path.replace(/\\/g, '/').replace(/^\.?\/+/, ''));
}

function fingerprint(bytes: Uint8Array): string {
  let h = 2166136261;
  const step = Math.max(1, Math.floor(bytes.length / 4096));
  for (let i = 0; i < bytes.length; i += step) h = Math.imul(h ^ bytes[i]!, 16777619);
  return `${bytes.length}:${h >>> 0}`;
}

/** Send a file to the worker unless it already has these bytes. `own` = the bytes may be transferred. */
function sendFile(path: string, bytes: Uint8Array, own = false): void {
  const fp = fingerprint(bytes);
  if (sentFiles.get(path) === fp || !worker) return;
  sentFiles.set(path, fp);
  const data = own ? bytes : bytes.slice();
  worker.postMessage({ type: 'addFile', path, bytes: data }, [data.buffer]);
}

/* ------------------------------------------------------------------ */
/* Files                                                               */

/** One installed cache asset (`GET /api/sound/cache` → `installed.soundfonts|voices[]`). */
interface CacheEntry {
  id: string;
  /** Cache-relative path of the main file (`soundfonts/<id>.sf2`, `voices/<id>/<id>.onnx`). */
  file: string;
}

interface CacheListing {
  defaultSoundfont?: string;
  installed?: { soundfonts?: CacheEntry[]; voices?: CacheEntry[] };
}

function entries(list: unknown): CacheEntry[] {
  return Array.isArray(list) ? list.filter((e): e is CacheEntry => !!e && typeof e === 'object' && typeof e.id === 'string' && typeof e.file === 'string') : [];
}

async function fetchCacheFile(path: string): Promise<Uint8Array> {
  const response = await fetch(apiUrl(`/sound/cache/file?path=${encodeURIComponent(path)}`), { credentials: 'same-origin' });
  if (!response.ok) throw new Error(`${path}: HTTP ${response.status}`);
  return new Uint8Array(await response.arrayBuffer());
}

/** Project files a document needs, following instrument `ref`s (depth-limited). */
async function collectProjectFiles(projectId: number, doc: SoundDoc, warnings: string[]): Promise<Map<string, Uint8Array>> {
  const out = new Map<string, Uint8Array>();
  const queue = referencedFiles(doc);
  while (queue.length && out.size < 200) {
    const path = queue.shift()!;
    if (out.has(path)) continue;
    try {
      const bytes = await readProjectFile(projectId, path);
      out.set(path, bytes);
      if (path.endsWith('.sound.json')) {
        try {
          for (const p of referencedFiles(JSON.parse(new TextDecoder().decode(bytes)))) if (!out.has(p)) queue.push(p);
        } catch {
          // The renderer reports a broken ref document itself.
        }
      }
    } catch (error) {
      warnings.push(`missing file ${path}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  return out;
}

async function collectCacheFiles(doc: SoundDoc, warnings: string[]): Promise<Map<string, Uint8Array>> {
  const out = new Map<string, Uint8Array>();
  const needsFont = usesSoundFont(doc);
  if (!needsFont && usedVoices(doc).length === 0) return out;
  let listing: CacheListing;
  try {
    listing = (await api.get<CacheListing>('/sound/cache')) ?? {};
  } catch {
    warnings.push('could not list the shared sound cache (SoundFont/voices)');
    return out;
  }
  // Cache-relative paths: SoundRenderer.addFile registers `soundfonts/…` and `voices/…` as cache files.
  const wanted: string[] = [];
  if (needsFont) {
    const fonts = entries(listing.installed?.soundfonts);
    const font = fonts.find((e) => e.id === listing.defaultSoundfont) ?? fonts[0];
    if (font) wanted.push(font.file);
    else warnings.push('the General MIDI SoundFont is not downloaded yet (Sound tab → Downloads, or `prism sound soundfont --download`)');
  }
  const voices = entries(listing.installed?.voices);
  for (const id of usedVoices(doc)) {
    const voice = voices.find((e) => e.id === id);
    if (voice) wanted.push(voice.file, `${voice.file}.json`);
    else warnings.push(`voice ${id} is not downloaded yet (Sound tab → Downloads); the built-in formant voice is used`);
  }
  for (const file of wanted) {
    // Cached assets never change under a path: the worker keeps them once sent.
    if (sentFiles.has(file)) continue;
    try {
      out.set(file, await fetchCacheFile(file));
    } catch (error) {
      warnings.push(`could not fetch cached ${file}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  return out;
}

/* ------------------------------------------------------------------ */
/* Render                                                              */

async function renderInBrowser(req: RenderRequest, warnings: string[]): Promise<SoundRenderResult> {
  req.onProgress?.('Starting sound engine', null);
  const reason = await startWorker();
  if (reason) throw Object.assign(new Error(reason), { unsupported: true });
  req.onProgress?.('Loading files', 0);
  const files = await collectProjectFiles(req.projectId, req.doc, warnings);
  const cached = await collectCacheFiles(req.doc, warnings);
  let i = 0;
  const total = files.size + cached.size;
  for (const [path, bytes] of files) {
    sendFile(path, bytes);
    req.onProgress?.('Loading files', total ? ++i / total : 1);
  }
  for (const [path, bytes] of cached) {
    sendFile(path, bytes, true);
    req.onProgress?.('Loading files', total ? ++i / total : 1);
  }
  req.onProgress?.('Rendering', null);
  const id = nextId++;
  const result = await new Promise<WorkerResult>((resolve, reject) => {
    pending.set(id, { resolve, reject });
    worker!.postMessage({
      type: 'render',
      id,
      json: JSON.stringify(req.doc),
      path: req.path,
      variant: req.variant ?? undefined,
      spectrogram: req.spectrogram ? SPECTROGRAM_SIZE : undefined,
    });
  });
  const spectrogram = result.spectrogram ? URL.createObjectURL(new Blob([result.spectrogram as BlobPart], { type: 'image/png' })) : null;
  return {
    audio: { left: result.left, right: result.right, rate: result.rate },
    analysis: (result.analysis as SoundAnalysis) ?? null,
    warnings: [...warnings, ...result.warnings],
    source: 'wasm',
    spectrogram,
    waveform: null,
  };
}

function looksBase64(text: string): boolean {
  return text.length > 256 && /^[A-Za-z0-9+/=\s]+$/.test(text.slice(0, 512));
}

function base64Bytes(text: string): Uint8Array {
  const clean = text.replace(/^data:[^,]*,/, '').replace(/\s+/g, '');
  const bin = atob(clean);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

function rawUrl(projectId: number, path: string): string {
  return apiUrl(`/projects/${projectId}/raw?path=${encodeURIComponent(path)}`);
}

function imageRef(projectId: number, value: unknown): string | null {
  if (typeof value !== 'string' || !value) return null;
  if (value.startsWith('data:')) return value;
  if (looksBase64(value)) return `data:image/png;base64,${value.replace(/\s+/g, '')}`;
  return `${rawUrl(projectId, value)}&t=${Date.now()}`;
}

/** Decode WAV bytes to planar stereo at the file's rate (WebAudio decoder). */
export async function decodeWav(bytes: Uint8Array): Promise<RenderedAudio> {
  const Ctx = globalThis.OfflineAudioContext;
  if (!Ctx) throw new Error('this browser cannot decode audio');
  const ctx = new Ctx(2, 1, 48000);
  const buffer = await ctx.decodeAudioData(bytes.slice().buffer);
  const left = buffer.getChannelData(0).slice();
  const right = buffer.numberOfChannels > 1 ? buffer.getChannelData(1).slice() : left.slice();
  return { left, right, rate: buffer.sampleRate };
}

async function renderOnServer(req: RenderRequest, warnings: string[]): Promise<SoundRenderResult> {
  req.onProgress?.('Saving', null);
  await req.save?.();
  req.onProgress?.('Rendering with prism (backend)', null);
  const out = await api.post<{ wav?: unknown; analysis?: unknown; spectrogramPng?: unknown; waveformPng?: unknown; warnings?: unknown }>(
    `/projects/${req.projectId}/sound/render`,
    { path: req.path, ...(req.variant ? { variant: req.variant } : {}) },
  );
  req.onProgress?.('Decoding', null);
  let bytes: Uint8Array;
  if (typeof out.wav === 'string' && looksBase64(out.wav)) bytes = base64Bytes(out.wav);
  else if (typeof out.wav === 'string' && out.wav) {
    const response = await fetch(rawUrl(req.projectId, out.wav), { credentials: 'same-origin' });
    if (!response.ok) throw new Error(`rendered clip ${out.wav}: HTTP ${response.status}`);
    bytes = new Uint8Array(await response.arrayBuffer());
  } else throw new Error('the backend render returned no audio');
  const audio = await decodeWav(bytes);
  let analysis: SoundAnalysis | null = null;
  if (out.analysis && typeof out.analysis === 'object') analysis = out.analysis as SoundAnalysis;
  else if (typeof out.analysis === 'string') {
    try {
      analysis = JSON.parse(out.analysis.trim().startsWith('{') ? out.analysis : new TextDecoder().decode(await readProjectFile(req.projectId, out.analysis))) as SoundAnalysis;
    } catch {
      analysis = null;
    }
  }
  return {
    audio,
    analysis,
    warnings: [...warnings, ...(Array.isArray(out.warnings) ? out.warnings.map(String) : [])],
    source: 'server',
    spectrogram: imageRef(req.projectId, out.spectrogramPng),
    waveform: imageRef(req.projectId, out.waveformPng),
  };
}

/**
 * Render an unsaved fragment document in the browser for the editors'
 * preview buttons (sound-api `setPreviewRenderer`): same worker and files as
 * the main render, no backend fallback.
 */
export async function renderPreviewDoc(
  projectId: number,
  doc: unknown,
  path: string,
): Promise<{ left: Float32Array; right: Float32Array; rate: number; warnings: string[]; analysis: unknown }> {
  const parsed = (typeof doc === 'string' ? JSON.parse(doc) : doc) as SoundDoc;
  const r = await renderInBrowser({ projectId, path, doc: parsed }, []);
  return { ...r.audio, warnings: r.warnings, analysis: r.analysis };
}

/** Render a document: browser engine first, backend when the browser cannot. */
export async function renderSound(req: RenderRequest): Promise<SoundRenderResult> {
  const warnings: string[] = [];
  if (req.serverOnly) return renderOnServer(req, warnings);
  let wasm: SoundRenderResult | null = null;
  try {
    wasm = await renderInBrowser(req, warnings);
  } catch (error) {
    const err = error as Error & { unsupported?: boolean };
    if (!err.unsupported && !UNSUPPORTED.test(err.message)) throw err;
    warnings.push(`browser render unavailable (${err.message}); rendered by the backend`);
  }
  if (wasm && !wasm.warnings.some((w) => UNSUPPORTED.test(w))) return wasm;
  try {
    return await renderOnServer(req, wasm ? [...wasm.warnings, 're-rendered by the backend (browser engine lacks a feature)'] : warnings);
  } catch (error) {
    if (wasm) return { ...wasm, warnings: [...wasm.warnings, `backend render failed: ${error instanceof Error ? error.message : String(error)}`] };
    throw error;
  }
}

/* ------------------------------------------------------------------ */
/* Analysis access                                                     */

function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

function pick(a: SoundAnalysis | null, keys: string[]): number | null {
  if (!a) return null;
  for (const key of keys) {
    const parts = key.split('.');
    let cur: unknown = a;
    for (const p of parts) cur = cur && typeof cur === 'object' ? (cur as Record<string, unknown>)[p] : undefined;
    const n = num(cur);
    if (n !== null) return n;
  }
  return null;
}

export interface AnalysisSummary {
  duration: number | null;
  peakDb: number | null;
  truePeakDb: number | null;
  lufs: number | null;
  rmsDb: number | null;
  centroid: number | null;
  pitchHz: number | null;
}

function toDb(linear: number): number {
  return linear > 0 ? 20 * Math.log10(linear) : -Infinity;
}

/** The headline numbers, tolerant of field naming; falls back to the buffer for peak/RMS. */
export function summarizeAnalysis(a: SoundAnalysis | null, audio: RenderedAudio | null): AnalysisSummary {
  let peakDb = pick(a, ['peakDbfs', 'peakDb', 'peak_db', 'samplePeakDb']);
  const peakLin = peakDb === null ? pick(a, ['peak', 'samplePeak']) : null;
  if (peakDb === null && peakLin !== null) peakDb = peakLin <= 1.5 && peakLin >= 0 ? toDb(peakLin) : peakLin;
  let rmsDb = pick(a, ['rmsDb', 'rms_db']);
  let duration = pick(a, ['duration']);
  if (audio && (peakDb === null || rmsDb === null || duration === null)) {
    let peak = 0;
    let sum = 0;
    const n = audio.left.length;
    for (let i = 0; i < n; i++) {
      const l = audio.left[i]!;
      const r = audio.right[i]!;
      peak = Math.max(peak, Math.abs(l), Math.abs(r));
      sum += (l * l + r * r) / 2;
    }
    peakDb ??= toDb(peak);
    rmsDb ??= n ? toDb(Math.sqrt(sum / n)) : -Infinity;
    duration ??= n / audio.rate;
  }
  return {
    duration,
    peakDb,
    truePeakDb: pick(a, ['truePeakDbtp', 'truePeakDb', 'truePeak']),
    lufs: pick(a, ['lufs', 'integratedLufs', 'loudness.integrated']),
    rmsDb,
    centroid: pick(a, ['spectral.centroidHz', 'spectralCentroid', 'centroidHz']),
    pitchHz: pick(a, ['pitch.medianHz', 'pitchHz']),
  };
}
